import { useEffect, useRef, useState, useCallback } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import {
  Power,
  Volume2,
  VolumeX,
  Fuel,
  Thermometer,
  AlertTriangle,
  Wrench,
  Lightbulb,
  ChevronUp,
  HardHat,
  Megaphone,
} from "lucide-react";
import sound from "@/lib/soundManager";
import confetti from "canvas-confetti";
import { GaugeDial } from "@/components/GaugeDial";
import { MiniMap } from "@/components/MiniMap";
import { RevealCards } from "@/components/RevealCards";
import { ArrivalInvite } from "@/components/ArrivalInvite";
import { RsvpDialog } from "@/components/RsvpDialog";
import { LeaderboardDialog } from "@/components/LeaderboardDialog";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Toaster } from "@/components/ui/sonner";

const GEARS = ["P", "N", "1", "2", "3", "D"];
const GEAR_MAX = { P: 0, N: 0, "1": 15, "2": 30, "3": 45, D: 60 };
const PROGRESS_K = 0.0014; // progress per (km/h * sec)
const MILESTONE_ATS = [0.35, 0.7]; // where party details unlock (ding!)

const MILESTONES = [
  { at: 0.35, kicker: "DESTINATION", label: "All roads lead to Cottage 837" },
  { at: 0.7, kicker: "PARTY TIME", label: "17:00 Hrs IST till 20:00 Hrs" },
];

const MAP_MILESTONES = [
  { at: 0.35, label: "Cottage 837" },
  { at: 0.7, label: "17:00 - 20:00 IST" },
  { at: 1.0, label: "Arrived!" },
];

// digger-dust palette: safety yellows, orange, dirt browns, metal + a hint of green
const DUST_COLORS = ["#FFD23F", "#F4B400", "#FF7A18", "#8B5E34", "#A9742F", "#C9D2DC", "#22C55E"];

function fireDiggerDust() {
  if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }
  // main burst from the ground up (like a digger kicking up dust)
  confetti({
    particleCount: 140,
    spread: 100,
    startVelocity: 55,
    gravity: 0.9,
    scalar: 1.05,
    origin: { x: 0.5, y: 0.7 },
    colors: DUST_COLORS,
  });
  // side cannons for a finale feel
  setTimeout(() => {
    confetti({ particleCount: 80, angle: 60, spread: 70, startVelocity: 50, origin: { x: 0, y: 0.75 }, colors: DUST_COLORS });
    confetti({ particleCount: 80, angle: 120, spread: 70, startVelocity: 50, origin: { x: 1, y: 0.75 }, colors: DUST_COLORS });
  }, 180);
  // lingering dusty drizzle from the top
  setTimeout(() => {
    confetti({ particleCount: 90, spread: 120, startVelocity: 30, gravity: 0.7, scalar: 0.8, origin: { x: 0.5, y: 0.2 }, colors: DUST_COLORS });
  }, 420);
}

function App() {
  const [engineOn, setEngineOn] = useState(false);
  const [gear, setGear] = useState("P");
  const [phase, setPhase] = useState("off"); // off | on | arrived
  const [muted, setMuted] = useState(sound.isMuted());
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [driveTimeMs, setDriveTimeMs] = useState(null);

  const [speed, setSpeed] = useState(0);
  const [rpm, setRpm] = useState(0);
  const [progress, setProgress] = useState(0);
  const [throttleActive, setThrottleActive] = useState(false);

  const engineOnRef = useRef(false);
  const gearRef = useRef("P");
  const throttleRef = useRef(false);
  const speedRef = useRef(0);
  const progressRef = useRef(0);
  const rafRef = useRef(null);
  const lastRef = useRef(0);
  const roadRef = useRef(null);
  const roadPosRef = useRef(0);
  const arrivedRef = useRef(false);
  const driveStartRef = useRef(null); // timestamp of first forward movement
  const dingedRef = useRef([]); // which milestones have dinged

  useEffect(() => {
    engineOnRef.current = engineOn;
  }, [engineOn]);
  useEffect(() => {
    gearRef.current = gear;
  }, [gear]);

  // animation loop
  useEffect(() => {
    const loop = (ts) => {
      if (!lastRef.current) lastRef.current = ts;
      const dt = Math.min(0.05, (ts - lastRef.current) / 1000);
      lastRef.current = ts;

      const maxSpeed = GEAR_MAX[gearRef.current] || 0;
      const driving = engineOnRef.current && throttleRef.current && maxSpeed > 0 && !arrivedRef.current;
      const target = driving ? maxSpeed : 0;

      const rate = target > speedRef.current ? 24 : 34;
      if (speedRef.current < target) speedRef.current = Math.min(target, speedRef.current + rate * dt);
      else speedRef.current = Math.max(target, speedRef.current - rate * dt);

      if (speedRef.current > 0.1 && progressRef.current < 1) {
        if (driveStartRef.current == null) driveStartRef.current = ts; // start timer on first movement
        progressRef.current = Math.min(1, progressRef.current + speedRef.current * PROGRESS_K * dt);
      }

      let r = 0;
      if (engineOnRef.current) {
        if (maxSpeed > 0) r = 0.9 + (speedRef.current / maxSpeed) * 6.1;
        else r = throttleRef.current ? 5.4 : 0.9;
      }
      sound.setEngineIntensity(engineOnRef.current ? r / 8 : 0);

      if (roadRef.current) {
        roadPosRef.current = (roadPosRef.current + speedRef.current * dt * 6) % 60;
        roadRef.current.style.backgroundPosition = `10px 0, calc(100% - 10px) 0, center ${roadPosRef.current}px`;
      }

      setSpeed(speedRef.current);
      setRpm(r);
      setProgress(progressRef.current);

      // milestone ding
      MILESTONE_ATS.forEach((at, i) => {
        if (!arrivedRef.current && progressRef.current >= at && !dingedRef.current[i]) {
          dingedRef.current[i] = true;
          sound.ding();
        }
      });

      if (progressRef.current >= 1 && !arrivedRef.current) {
        arrivedRef.current = true;
        throttleRef.current = false;
        setThrottleActive(false);
        const elapsed = driveStartRef.current != null ? Math.round(ts - driveStartRef.current) : 0;
        setDriveTimeMs(elapsed);
        setPhase("arrived");
        sound.cheer();
        fireDiggerDust();
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const handleIgnition = useCallback(async () => {
    if (engineOn) return;
    await sound.init();
    sound.resume();
    sound.startEngine();
    setEngineOn(true);
    setPhase("on");
    setGear("N");
    driveStartRef.current = null;
    dingedRef.current = [];
    setDriveTimeMs(null);
  }, [engineOn]);

  const handleGearChange = useCallback(
    (val) => {
      if (!val || !engineOn || arrivedRef.current) return;
      setGear(val);
      sound.gearShift();
    },
    [engineOn]
  );

  const startThrottle = useCallback(() => {
    if (!engineOnRef.current || arrivedRef.current) return;
    sound.resume();
    throttleRef.current = true;
    setThrottleActive(true);
  }, []);

  const stopThrottle = useCallback(() => {
    throttleRef.current = false;
    setThrottleActive(false);
  }, []);

  const toggleMute = useCallback(() => {
    setMuted(sound.setMuted(!sound.isMuted()));
  }, []);

  const honk = useCallback(async () => {
    await sound.init();
    sound.resume();
    sound.horn();
  }, []);

  const replay = useCallback(() => {
    arrivedRef.current = false;
    progressRef.current = 0;
    speedRef.current = 0;
    throttleRef.current = false;
    setThrottleActive(false);
    setProgress(0);
    setSpeed(0);
    setGear("N");
    setPhase("on");
    driveStartRef.current = null;
    dingedRef.current = [];
    setDriveTimeMs(null);
  }, []);

  // keyboard throttle (space)
  useEffect(() => {
    const down = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        startThrottle();
      }
    };
    const up = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        stopThrottle();
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [startThrottle, stopThrottle]);

  const inDriveGear = GEAR_MAX[gear] > 0;
  let hint = "Press START to fire up the excavator";
  if (engineOn && phase !== "arrived") {
    if (!inDriveGear) hint = "Shift into a gear (try 1), then hold THROTTLE";
    else if (gear === "1" && progress < 0.2) hint = "Shift up (2, 3, D) to go faster!";
    else hint = "Hold THROTTLE \u2014 head for Cottage 837";
  }

  const lights = [
    { icon: Lightbulb, on: engineOn },
    { icon: Fuel, on: engineOn },
    { icon: Thermometer, on: engineOn && speed > 30 },
    { icon: Wrench, on: !engineOn },
    { icon: AlertTriangle, on: engineOn && !inDriveGear },
  ];

  return (
    <div className="App premium-bg selection-orange h-[100dvh] w-full overflow-hidden flex items-center justify-center p-2 sm:p-4">
      <Toaster position="top-center" richColors />

      <div className="cockpit noise-overlay relative w-full max-w-[460px] h-full sm:h-auto sm:max-h-[94dvh] rounded-[26px] flex flex-col overflow-hidden">
        {/* top hazard accent */}
        <div className="h-1.5 hazard-strip shrink-0" />

        {/* header */}
        <header className="shrink-0 flex items-center justify-between gap-2 px-3.5 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--hazard-yellow))] text-[#0e1217]">
              <HardHat size={20} />
            </span>
            <div className="min-w-0">
              <h1 data-testid="app-title" className="font-display tracking-[0.12em] text-xl text-white leading-none truncate">
                ADVIT'S EXCAVATOR DASH
              </h1>
              <p className="text-[10px] text-white/50 font-medium leading-tight mt-0.5">Start | shift | uncover the party</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              onClick={honk}
              aria-label="Horn"
              data-testid="horn-button"
              className="h-9 w-9 p-0 rounded-xl bg-[hsl(var(--hazard-yellow))] hover:bg-[hsl(43_100%_55%)] text-[#0e1217] chunky-btn"
            >
              <Megaphone size={17} />
            </Button>
            <Button
              onClick={toggleMute}
              aria-label={muted ? "Unmute" : "Mute"}
              data-testid="mute-toggle"
              className="h-9 w-9 p-0 rounded-xl glass text-white hover:bg-white/10 chunky-btn"
            >
              {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
            </Button>
          </div>
        </header>

        {/* content */}
        <div className="flex-1 min-h-0 flex flex-col gap-2 px-3.5 pb-3.5">
          {phase === "arrived" ? (
            <ArrivalInvite
              onRsvp={() => setRsvpOpen(true)}
              onReplay={replay}
              driveTimeMs={driveTimeMs}
              onLeaderboard={() => setLeaderboardOpen(true)}
            />
          ) : (
            <>
              {/* instruments — flexible region that absorbs spare height */}
              <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-2">
                {/* windshield road */}
                <div className="short-hide shrink-0 rounded-xl overflow-hidden border border-white/10">
                  <div ref={roadRef} className="road-strip h-10 sm:h-12 w-full relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <motion.div animate={engineOn ? { y: [0, -2, 0] } : { y: 0 }} transition={{ repeat: Infinity, duration: 0.25 }}>
                        <svg width="52" height="36" viewBox="0 0 70 48" aria-hidden>
                          <g fill="hsl(var(--hazard-yellow))" stroke="#0e1217" strokeWidth="2">
                            <rect x="6" y="26" width="42" height="12" rx="3" />
                            <rect x="14" y="16" width="18" height="12" rx="2" />
                            <line x1="30" y1="20" x2="62" y2="6" stroke="#0e1217" strokeWidth="4" />
                            <path d="M58 2 l10 4 l-4 9 z" />
                            <circle cx="16" cy="40" r="6" fill="#0e1217" />
                            <circle cx="38" cy="40" r="6" fill="#0e1217" />
                          </g>
                        </svg>
                      </motion.div>
                    </div>
                  </div>
                </div>

                {/* gauges — this block flexes to fill remaining space */}
                <div className="flex-1 min-h-[96px] glass rounded-2xl p-2 grid grid-cols-2 gap-2">
                  <GaugeDial value={speed} min={0} max={60} majorStep={10} label="SPEED" unit="km/h" size={150} testId="speedometer" />
                  <GaugeDial value={rpm} min={0} max={8} majorStep={1} label="RPM x1000" unit="rpm" redlineStart={6} needleColor="hsl(0 84% 60%)" size={150} testId="rpm-gauge" />
                </div>

                {/* warning lights */}
                <div className="short-hide shrink-0 glass-2 rounded-xl px-3 py-1.5 flex items-center justify-between" data-testid="warning-lights">
                  {lights.map((l, i) => {
                    const Ic = l.icon;
                    return (
                      <span
                        key={i}
                        className={`inline-flex items-center justify-center ${
                          l.on ? "text-[hsl(var(--hazard-yellow))] drop-shadow-[0_0_8px_rgba(255,210,63,0.5)]" : "text-white/25"
                        }`}
                      >
                        <Ic size={17} />
                      </span>
                    );
                  })}
                </div>

                {/* route map */}
                <div className="shrink-0">
                  <MiniMap progress={progress} milestones={MAP_MILESTONES} />
                </div>

                {/* reveal pills */}
                <div className="shrink-0">
                  <RevealCards items={MILESTONES} progress={progress} />
                </div>
              </div>

              {/* controls dock — always pinned, never clipped */}
              <div className="shrink-0 space-y-2">
                {/* hint */}
                <div className="flex items-center gap-2 rounded-lg glass-2 px-2.5 py-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--hazard-yellow))] shrink-0 animate-pulse" />
                  <p data-testid="hint-text" className="text-[11px] text-white/75 font-medium leading-tight">{hint}</p>
                </div>

                {/* gear shifter */}
                <ToggleGroup
                  type="single"
                  value={gear}
                  onValueChange={handleGearChange}
                  className="grid grid-cols-6 gap-1.5"
                  data-testid="gear-shifter"
                  disabled={!engineOn}
                >
                  {GEARS.map((g) => (
                    <ToggleGroupItem
                      key={g}
                      value={g}
                      data-testid={`gear-${g}`}
                      disabled={!engineOn}
                      className="h-10 rounded-lg font-display text-lg tracking-wider data-[state=on]:bg-[hsl(var(--orange))] data-[state=on]:text-white glass-2 text-white/75 disabled:opacity-40"
                    >
                      {g}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>

                {/* start + throttle */}
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    onClick={handleIgnition}
                    disabled={engineOn}
                    data-testid="ignition-start-button"
                    className={`col-span-1 h-14 chunky-btn font-display tracking-wider text-base px-1 ${
                      engineOn ? "bg-[hsl(var(--teal))] text-white" : "bg-[hsl(var(--hazard-yellow))] text-[#0e1217] ignition-pulse"
                    }`}
                  >
                    <Power size={18} className="mr-0.5" />
                    {engineOn ? "ON" : "START"}
                  </Button>

                  <button
                    type="button"
                    data-testid="throttle-button"
                    disabled={!engineOn}
                    onPointerDown={startThrottle}
                    onPointerUp={stopThrottle}
                    onPointerLeave={stopThrottle}
                    onPointerCancel={stopThrottle}
                    className={`col-span-2 h-14 select-none touch-none chunky-btn rounded-xl font-display tracking-[0.15em] text-xl flex items-center justify-center gap-1.5 ${
                      !engineOn
                        ? "bg-white/5 text-white/25 cursor-not-allowed"
                        : throttleActive
                        ? "bg-[hsl(var(--orange))] text-white"
                        : "bg-[hsl(var(--hazard-yellow))] text-[#0e1217]"
                    }`}
                  >
                    <ChevronUp size={20} className={throttleActive ? "animate-bounce" : ""} />
                    {throttleActive ? "DIGGING IN..." : "HOLD TO DRIVE"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <RsvpDialog open={rsvpOpen} onOpenChange={setRsvpOpen} />
      <LeaderboardDialog open={leaderboardOpen} onOpenChange={setLeaderboardOpen} timeMs={driveTimeMs} />
    </div>
  );
}

export default App;
